/**
 * OperationInspectorTanTable — inspector for tantable nodes (ADR-0070 §3.2).
 *
 * ADR-0129 — on the shared spine: Info (node label) · Setup (row selection +
 * spec editor) · Data (live render) · AI (viz copilot, frame-filling chat). The
 * persistent Run / Export / last-run toolbar sits above the tab rack (a
 * node-global action bar, not a slot).
 *
 * The chat panel posts to the operation_viz session endpoint, which routes to
 * the existing `viz` orchestrator with `nodeType=tantable` (Phase 5.5 wiring).
 * Returned tantable.v1 specs are auto-applied to `config.spec_tantable`.
 *
 * Run executes the node and pulls the cached `render_out` for preview;
 * Export ▾ surfaces the existing Excel + CSV writers.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { aiRouteErrorCode } from "../../lib/aiRouteError.js";
import type { Node } from "@xyflow/react";
import type { OperationCanvasNodeData } from "../OperationCanvasNode.js";
import { apiFetch, ApiRequestError, apiFetchBlob, triggerBlobDownload } from "../../lib/api.js";
import { useToast } from "../Toast.js";
import { useConfirm } from "../ConfirmDialog.js";
import { useAiChatThread, type AiChatMessage } from "../../hooks/useAiChatThread.js";
import { useOperationRun } from "../../hooks/useOperationRun.js";
import {
  ChatMessageList,
  ChatComposer,
  ChatThinkingIndicator,
  ChatErrorBanner
} from "../aiChat/index.js";
import { TanTableViz } from "../TanTableViz.js";
import { exportTanTableToCsv } from "../../lib/tanTableExport.js";
import type { TanTableRenderModel, TanTableVizSpecV1 } from "../../lib/tanTableSpec.js";
import { InspectorTabs } from "../inspector/InspectorTabs.js";

const INSPECTOR_WIDTH_STYLE: React.CSSProperties = { width: "100%", maxWidth: "100%", minWidth: 0 };

type ChatMessage = AiChatMessage;
type InteractionMode = "agent" | "ask";

interface Props {
  selectedNode: Node<OperationCanvasNodeData>;
  operationId: string;
  setNodeData: (nodeId: string, updater: (data: OperationCanvasNodeData) => OperationCanvasNodeData) => void;
  flushAndSave: () => Promise<boolean>;
  tenantAiModels?: Array<{ model_id: string; status: string }>;
  selectedAiModelId?: string;
  onTenantAiModelChange?: (modelId: string) => void;
  setRunStatus?: (nodeIds: string[], status: "running" | "success" | "error" | "idle") => void;
}

const sessionLastRunByNodeId = new Map<string, { lastRunAt: string; lastRowCount: number | null }>();

export function OperationInspectorTanTable({
  selectedNode,
  operationId,
  setNodeData,
  flushAndSave,
  tenantAiModels = [],
  selectedAiModelId,
  onTenantAiModelChange,
  setRunStatus,
}: Props) {
  const toast = useToast();
  const confirm = useConfirm();
  const runController = useOperationRun();
  const nodeId = selectedNode.id;

  const config = (selectedNode.data.config ?? {}) as Record<string, unknown>;
  const currentSpec = config.spec_tantable as TanTableVizSpecV1 | undefined;
  const currentSpecJson = currentSpec ? JSON.stringify(currentSpec, null, 2) : "";

  const [interactionMode, setInteractionMode] = useState<InteractionMode>("agent");
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // ─── spec editor ───
  const [specDraft, setSpecDraft] = useState<string>(currentSpecJson);
  const [specError, setSpecError] = useState<string | null>(null);
  const specSyncRef = useRef<string>(currentSpecJson);
  const specTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync the textarea when the spec changes from outside (e.g. AI agent applies a new spec).
  // Skip the sync if the user is currently editing, so we don't clobber in-flight typing.
  useEffect(() => {
    if (currentSpecJson === specSyncRef.current) return;
    specSyncRef.current = currentSpecJson;
    if (document.activeElement !== specTextareaRef.current) {
      setSpecDraft(currentSpecJson);
      setSpecError(null);
    }
  }, [currentSpecJson]);

  const handleSpecChange = useCallback((value: string) => {
    setSpecDraft(value);
    if (value.trim() === "") {
      setSpecError(null);
      return;
    }
    try {
      const parsed = JSON.parse(value);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        setSpecError("Spec must be a JSON object.");
      } else {
        setSpecError(null);
      }
    } catch (e) {
      setSpecError(e instanceof Error ? e.message : "Invalid JSON.");
    }
  }, []);

  const commitSpec = useCallback(async () => {
    if (specError) return;
    if (specDraft === specSyncRef.current) return;
    let nextSpec: TanTableVizSpecV1 | undefined;
    if (specDraft.trim() === "") {
      nextSpec = undefined;
    } else {
      try {
        nextSpec = JSON.parse(specDraft) as TanTableVizSpecV1;
      } catch (e) {
        setSpecError(e instanceof Error ? e.message : "Invalid JSON.");
        return;
      }
    }
    setNodeData(nodeId, (d) => {
      const nextConfig = { ...(d.config ?? {}) } as Record<string, unknown>;
      if (nextSpec === undefined) delete nextConfig.spec_tantable;
      else nextConfig.spec_tantable = nextSpec;
      return { ...d, config: nextConfig };
    });
    specSyncRef.current = specDraft;
    const saved = await flushAndSave();
    if (saved) toast.push({ kind: "success", message: "TanTable spec saved" });
  }, [specDraft, specError, setNodeData, nodeId, flushAndSave, toast]);

  // ─── render preview ───
  const [renderModel, setRenderModel] = useState<TanTableRenderModel | null>(null);
  const [renderLoading, setRenderLoading] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);

  const loadRenderModel = useCallback(async () => {
    setRenderLoading(true);
    setRenderError(null);
    try {
      const data = await apiFetch<{ renderModel?: unknown }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/cache?port_id=render_out`
      );
      const rm = data.renderModel as Record<string, unknown> | undefined;
      if (rm && rm.kind === "tantable" && Array.isArray(rm.columns) && Array.isArray(rm.rows)) {
        setRenderModel(rm as unknown as TanTableRenderModel);
      } else {
        setRenderModel(null);
      }
    } catch (e) {
      if (e instanceof ApiRequestError && e.status === 404) {
        setRenderModel(null);
      } else {
        setRenderError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setRenderLoading(false);
    }
  }, [operationId, nodeId]);

  useEffect(() => { void loadRenderModel(); }, [loadRenderModel]);

  // ─── run preview ───
  const [runBusy, setRunBusy] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const lastRunStored = sessionLastRunByNodeId.get(nodeId) ?? null;
  const [lastRunAt, setLastRunAt] = useState<string | null>(lastRunStored?.lastRunAt ?? null);
  const [lastRowCount, setLastRowCount] = useState<number | null>(lastRunStored?.lastRowCount ?? null);

  const runNode = useCallback(async () => {
    setRunBusy(true);
    setRunError(null);
    setRenderError(null);
    setRunStatus?.([nodeId], "running");
    try {
      const saved = await flushAndSave();
      if (!saved) { setRunStatus?.([nodeId], "idle"); setRunBusy(false); return; }
      // ADR-0128 — async run: POST → poll runs/:runId; the render model is read
      // from the node cache (`render_out`) after the run, not from the receipt.
      await runController.startRun({
        operationId,
        nodeId,
        forceRefresh: false,
        onComplete: async (_run, nodes) => {
          await loadRenderModel();
          const target = nodes.find((n) => n.node_id === nodeId) ?? null;
          const runAt = target?.finished_at ?? new Date().toISOString();
          const rowCount = target?.row_count ?? null;
          setLastRunAt(runAt);
          setLastRowCount(rowCount);
          sessionLastRunByNodeId.set(nodeId, { lastRunAt: runAt, lastRowCount: rowCount });
          setRunStatus?.([nodeId], "success");
          toast.push({ kind: "success", message: `Executed — ${(rowCount ?? 0).toLocaleString()} rows` });
        },
        onError: (error) => {
          setRunError(error ?? "Run failed.");
          setRunStatus?.([nodeId], "error");
        },
        onCancelled: () => { setRunStatus?.([nodeId], "idle"); },
        onFailure: (err) => {
          setRunError(err instanceof Error ? err.message : String(err));
          setRunStatus?.([nodeId], "error");
        },
      });
    } finally {
      setRunBusy(false);
    }
  }, [operationId, nodeId, flushAndSave, setRunStatus, toast, runController, loadRenderModel]);

  // ─── export ───
  const filenameSeed = selectedNode.data.label?.trim() || "tantable";
  const handleExportExcel = useCallback(async () => {
    if (!renderModel) return;
    try {
      // ADR-0099 Option B: server builds the workbook.
      const { blob, filename, contentType } = await apiFetchBlob(
        "/api/tenant/export-table-workbook",
        { body: { table_render_model: renderModel, title: filenameSeed } },
      );
      triggerBlobDownload(
        new Blob([blob], { type: contentType }),
        filename ?? `${filenameSeed}.xlsx`,
      );
      setExportMenuOpen(false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [renderModel, filenameSeed, toast]);
  const handleExportCsv = useCallback(() => {
    if (!renderModel) return;
    try {
      exportTanTableToCsv(renderModel, renderModel.rows, null, filenameSeed);
      setExportMenuOpen(false);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }, [renderModel, filenameSeed, toast]);

  // ─── AI chat ───
  // Session lifecycle (load-latest + poll-while-trailing-user) lives in
  // `useAiChatThread` per spec §5.1 / ADR-0076 expansion. Local state below
  // covers active-mount send + composer draft only.
  const {
    sessionId,
    setSessionId,
    messages,
    setMessages,
    replyPending,
    error: chatThreadError,
    resetChat,
    cancelChat,
  } = useAiChatThread({
    threadKey: `${operationId}/${nodeId}/tantable`,
    loadLatest: useCallback(async () => {
      const res = await apiFetch<{
        session: { id: string; created_at?: string };
        messages: Array<{ id: string; role: string; content: string; created_at?: string }>;
      }>(`/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions/latest`);
      return {
        sessionId: res.session.id,
        messages: res.messages.map((m) => ({
          id: m.id,
          role: m.role as AiChatMessage["role"],
          content: m.content,
          created_at: m.created_at ?? "",
        })),
      };
    }, [operationId, nodeId]),
    createNewSession: useCallback(async () => {
      const res = await apiFetch<{ session: { id: string } }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions`,
        { method: "POST", body: {} }
      );
      return { sessionId: res.session.id };
    }, [operationId, nodeId]),
    cancelChat: useCallback(async (sid: string) => {
      await apiFetch(
        `/api/tenant/ai/sessions/${encodeURIComponent(sid)}/abort`,
        { method: "POST", body: {} }
      );
    }, []),
  });
  const [draftMessage, setDraftMessage] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  // ADR-0207 §3.4: the hub's error code, for the admin's link under an AI-route refusal.
  const [chatErrorCode, setChatErrorCode] = useState<string | null>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [messages]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const res = await apiFetch<{ session: { id: string } }>(
      `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions`,
      { method: "POST", body: {} }
    );
    setSessionId(res.session.id);
    return res.session.id;
  }, [sessionId, operationId, nodeId]);

  const sendChat = useCallback(async () => {
    const text = draftMessage.trim();
    if (!text) return;
    setChatBusy(true);
    setChatError(null); setChatErrorCode(null);
    const optimisticId = `local-${Date.now()}`;
    setMessages((prev) => [...prev, { id: optimisticId, role: "user", content: text, created_at: new Date().toISOString() }]);
    setDraftMessage("");
    try {
      const sid = await ensureSession();
      const saved = await flushAndSave();
      if (!saved) { setChatError("Failed to save before sending"); return; }
      const data = await apiFetch<{
        assistantMessage: { id: string; displayText: string };
        renderModel?: unknown;
        meta?: { persistableVizSpec?: unknown; responseMode?: string };
      }>(
        `/api/tenant/operations/${encodeURIComponent(operationId)}/nodes/${encodeURIComponent(nodeId)}/ai/viz/sessions/${encodeURIComponent(sid)}/messages`,
        {
          method: "POST",
          body: {
            message: text,
            viz_type: "tantable",
            interactionMode,
            ...(selectedAiModelId ? { model: selectedAiModelId } : {}),
          },
        }
      );
      setMessages((prev) => [...prev, { id: data.assistantMessage.id, role: "assistant", content: data.assistantMessage.displayText, created_at: new Date().toISOString() }]);
      // Auto-apply the merged tantable spec when the agent returned one.
      const newSpec = data.meta?.persistableVizSpec;
      if (newSpec && typeof newSpec === "object") {
        setNodeData(nodeId, (d) => ({ ...d, config: { ...d.config, spec_tantable: newSpec } }));
        await flushAndSave();
        toast.push({ kind: "success", message: "TanTable spec updated" });
      }
      const rm = data.renderModel as Record<string, unknown> | undefined;
      if (rm && rm.kind === "tantable" && Array.isArray(rm.columns) && Array.isArray(rm.rows)) {
        setRenderModel(rm as unknown as TanTableRenderModel);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setChatError(msg); setChatErrorCode(aiRouteErrorCode(e));
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setDraftMessage(text);
    } finally {
      setChatBusy(false);
    }
  }, [draftMessage, ensureSession, flushAndSave, interactionMode, operationId, nodeId, selectedAiModelId, setNodeData, toast]);

  // ─── slot content ───
  const infoContent = (
    <div className="card" style={{ display: "grid", gap: 10 }}>
      <div className="field">
        <label className="label">Node Name</label>
        <input
          className="input"
          type="text"
          value={selectedNode.data.label}
          onChange={(e) => setNodeData(nodeId, (d) => ({ ...d, label: e.target.value }))}
          onBlur={() => void flushAndSave()}
          placeholder="Unnamed"
        />
      </div>
    </div>
  );

  const setupContent = (
    <div style={{ display: "grid", gap: 10 }}>
      <div className="card" style={{ display: "grid", gap: 10 }}>
        <label className="field">
          <span className="label">Row selection</span>
          <select
            className="select"
            value={currentSpec?.rowSelection ?? "none"}
            disabled={!currentSpec}
            onChange={(e) => {
              const val = e.target.value as "none" | "single" | "multi";
              setNodeData(nodeId, (d) => {
                const cfg = { ...(d.config ?? {}) } as Record<string, unknown>;
                const prev = cfg.spec_tantable as TanTableVizSpecV1 | undefined;
                if (!prev) return d;
                cfg.spec_tantable = { ...prev, rowSelection: val };
                return { ...d, config: cfg };
              });
              void flushAndSave();
            }}
          >
            <option value="none">None</option>
            <option value="single">Single</option>
            <option value="multi">Multi</option>
          </select>
          <div className="help">
            {currentSpec
              ? "When a row is selected here, page widgets bound to this widget receive the row's values as filters."
              : "Define a spec below (or via the AI tab) before enabling row selection."}
          </div>
        </label>
      </div>

      <div className="card">
        <details>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>
            Spec
          </summary>
          <div style={{ marginTop: 8 }}>
            <textarea
              ref={specTextareaRef}
              className="input mono"
              value={specDraft}
              onChange={(e) => handleSpecChange(e.target.value)}
              onBlur={() => void commitSpec()}
              spellCheck={false}
              placeholder='{"version":"tantable.v1","columns":[…]}'
              style={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                fontSize: 11,
                height: 320,
                padding: 8,
                resize: "vertical",
                whiteSpace: "pre",
              }}
            />
            {specError ? (
              <div className="help" style={{ color: "var(--danger-text)" }}>{specError}</div>
            ) : null}
          </div>
        </details>
      </div>
    </div>
  );

  const dataContent = (
    <div className="card">
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview</div>
      {renderError ? <div className="help" style={{ color: "var(--danger-text)" }}>{renderError}</div> : null}
      {renderLoading ? <div className="muted">Loading…</div> : null}
      {!renderLoading && !renderModel ? (
        <div className="muted" style={{ fontSize: 13, fontStyle: "italic" }}>
          {currentSpec?.columns && currentSpec.columns.length > 0
            ? "No preview yet — click Run to render the table."
            : "No spec yet — describe the table you want in the AI tab, then click Run."}
        </div>
      ) : null}
      {renderModel ? (
        <div className="inspectorTanTableHost">
          <TanTableViz model={renderModel} title={filenameSeed} />
        </div>
      ) : null}
    </div>
  );

  const aiContent = (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 8 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
        <div style={{ fontWeight: 600 }}>Atlas TanTable agent</div>
        <div style={{ flex: 1 }} />
        {/*
          Reset chat — abandons the current session and starts fresh.
          Per spec §10.4 + user request: gives users an explicit escape
          hatch when threads grow too long, when context drifts, or
          when a session deadlocks (e.g. hub-restart-stranded thread
          per spec §3.6.3).
        */}
        <button
          type="button"
          className="btn btnSm"
          onClick={() =>
            void (async () => {
              const ok = await confirm.confirm({
                title: "Reset this chat?",
                detail:
                  "The current conversation is cleared from view and a fresh session starts. Past messages stay on the server but are no longer visible here.",
                confirmLabel: "Reset chat",
              });
              if (!ok) return;
              await resetChat();
            })()
          }
          disabled={messages.length === 0}
          title="Start a new conversation. Past messages stay on the server but are no longer visible here. Available even mid-thinking — that's the deadlock escape hatch."
        >
          Reset chat
        </button>
        <select
          className="select"
          style={{ width: 110 }}
          value={interactionMode}
          onChange={(e) => setInteractionMode(e.target.value as InteractionMode)}
          title="Agent mode auto-applies a new spec; Ask mode answers questions only"
        >
          <option value="agent">Agent</option>
          <option value="ask">Ask</option>
        </select>
        {tenantAiModels.length > 0 && selectedAiModelId ? (
          <select
            className="select"
            style={{ width: 200 }}
            value={selectedAiModelId}
            onChange={(e) => onTenantAiModelChange?.(e.target.value)}
            disabled={!onTenantAiModelChange}
            title="Model used for the next AI message"
          >
            {tenantAiModels.map((m) => (
              <option key={m.model_id} value={m.model_id}>
                {m.model_id}
                {m.status === "deprecated" ? " (deprecated)" : ""}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div ref={chatScrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 4 }}>
        <ChatMessageList
          messages={messages}
          assistantLabel="Atlas TanTable agent"
          emptyState={
            <div className="muted" style={{ fontSize: 13, fontStyle: "italic" }}>
              Tell the agent what table you want — e.g. "Show revenue by month with a year-over-year delta column."
            </div>
          }
          trailingNode={<ChatThinkingIndicator visible={replyPending} />}
        />
      </div>
      <ChatErrorBanner message={chatError ?? chatThreadError} code={chatError ? chatErrorCode : null} />
      <ChatComposer
        draft={draftMessage}
        setDraft={setDraftMessage}
        onSend={() => void sendChat()}
        onCancel={() => void cancelChat()}
        inFlight={chatBusy || replyPending}
        placeholder="Describe the table you want… (Enter to send, Shift+Enter for newline)"
        minRows={3}
        maxRows={12}
      />
    </div>
  );

  // ─── render ───
  return (
    <div style={{ ...INSPECTOR_WIDTH_STYLE, display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Persistent action bar — node-global, above the tab rack. */}
      <div style={{ display: "flex", gap: 4, alignItems: "center", borderBottom: "1px solid var(--line)", paddingBottom: 6, marginBottom: 8, flexWrap: "wrap", flexShrink: 0 }}>
        <div className="muted" style={{ fontSize: 12, marginRight: 8 }}>
          {runBusy ? "Running…" : lastRunAt ? (
            <>Last run: {new Date(lastRunAt).toLocaleString()}{typeof lastRowCount === "number" ? ` · ${lastRowCount.toLocaleString()} rows` : ""}</>
          ) : "Not yet executed"}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ position: "relative" }}>
          <button
            className="btn btnSm"
            type="button"
            onClick={() => setExportMenuOpen((o) => !o)}
            disabled={!renderModel}
            title={renderModel ? "Export the current rendered table" : "Run the node first to export"}
          >
            Export ▾
          </button>
          {exportMenuOpen && renderModel ? (
            <div style={{ position: "absolute", top: "100%", right: 0, background: "var(--bg, white)", border: "1px solid var(--line)", borderRadius: 4, padding: 4, zIndex: 10, boxShadow: "0 2px 8px rgba(0,0,0,0.1)", display: "grid", gap: 2, minWidth: 120 }}>
              <button className="btn btnSm" type="button" onClick={() => void handleExportExcel()}>Excel (.xlsx)</button>
              <button className="btn btnSm" type="button" onClick={handleExportCsv}>CSV</button>
            </div>
          ) : null}
        </div>
        <button className="btn btnPrimary btnSm" type="button" onClick={() => void runNode()} disabled={runBusy}>
          {runBusy ? "Running…" : "▶ Run"}
        </button>
      </div>

      {runError ? <div className="help" style={{ color: "var(--danger-text)", flexShrink: 0, marginBottom: 8 }}>{runError}</div> : null}

      <InspectorTabs
        surfaceId="operationEditor"
        tabGroupId="tantable"
        defaultSlot="setup"
        tabs={[
          { slot: "info", content: infoContent },
          { slot: "setup", content: setupContent },
          { slot: "data", content: dataContent },
          { slot: "ai", fill: true, content: aiContent },
        ]}
      />
    </div>
  );
}
